import { Head, router } from "@inertiajs/react"
import { RefreshCw01 } from "@untitledui/icons"

import { FilterChip, FilterRow } from "@/components/admin/filter-chip"
import { PageHeader } from "@/components/console/page-header"
import { SchoolLink } from "@/components/console/school-link"
import { Button } from "@/components/ui/button"
import { Table, TableCard } from "@/components/ui/table"
import { AdminLayout } from "@/layouts/admin-layout"
import {
  adminSalesforceJobPath,
  adminSalesforceJobsPath,
  syncAdminSalesforceJobsPath,
} from "@/routes"
import type { SchoolReference } from "@/types"

import type { JobStatusColor, WindowPreset } from "./types"

interface Job {
  id: number
  salesforce_id: string
  name: string | null
  program_name: string | null
  /** The account as a reference; null for an orphan job. */
  school: SchoolReference | null
  start_time: string | null
  status: string | null
  status_color: JobStatusColor
  region: string | null
  expected_attendance: number | null
}

interface Props {
  jobs: Job[]
  filters: { status: string | null; window: string }
  available_filters: {
    statuses: string[]
    record_type: string
    windows: WindowPreset[]
  }
  counts: { total: number }
}

export default function AdminSalesforceJobsIndex({
  jobs,
  filters,
  available_filters,
  counts,
}: Props) {
  const syncNow = () => {
    router.post(syncAdminSalesforceJobsPath(), {}, { preserveScroll: true })
  }

  const visitWith = (next: Partial<Props["filters"]>) => {
    const merged = { ...filters, ...next }
    const data: Record<string, string> = { window: merged.window }
    if (merged.status) data.status = merged.status
    router.visit(adminSalesforceJobsPath(), {
      data,
      preserveState: true,
      preserveScroll: true,
      only: ["jobs", "filters", "counts"],
    })
  }

  const preset = available_filters.windows.find((w) => w.key === filters.window)
  const tokens = [
    preset &&
      filters.window !== "full" && {
        label: `window: ${preset.label.toLowerCase()}`,
        onClear: () => visitWith({ window: "full" }),
      },
    filters.status && {
      label: `status: ${filters.status.toLowerCase()}`,
      onClear: () => visitWith({ status: null }),
    },
  ].filter((token): token is { label: string; onClear: () => void } =>
    Boolean(token),
  )

  return (
    <AdminLayout>
      <Head title="Salesforce Jobs" />
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 lg:px-8">
        <PageHeader
          crumbs={[{ label: "salesforce" }, { label: "jobs" }]}
          title="Salesforce jobs"
          description="The mirror of upcoming school programs, refreshed from Salesforce."
          actions={
            <Button
              size="md"
              color="secondary"
              iconLeading={RefreshCw01}
              onPress={syncNow}
            >
              Sync now
            </Button>
          }
        />

        <div className="flex flex-col gap-3">
          <FilterRow label="Window">
            {available_filters.windows.map(({ key, label }) => (
              <FilterChip
                key={key}
                active={filters.window === key}
                onClick={() => visitWith({ window: key })}
              >
                {label}
              </FilterChip>
            ))}
          </FilterRow>
          <FilterRow label="Status">
            <FilterChip
              active={!filters.status}
              onClick={() => visitWith({ status: null })}
            >
              All
            </FilterChip>
            {available_filters.statuses.map((status) => (
              <FilterChip
                key={status}
                active={filters.status === status}
                onClick={() => visitWith({ status })}
              >
                {status}
              </FilterChip>
            ))}
          </FilterRow>
        </div>

        <TableCard.Root bleed>
          <TableCard.Caption
            title="Jobs"
            count={`${counts.total} ${counts.total === 1 ? "job" : "jobs"} · ${available_filters.record_type.toLowerCase()}`}
            tokens={tokens}
            note="start ↑"
          />
          {jobs.length === 0 ? (
            <TableCard.Empty
              action={
                (filters.status || filters.window !== "full") && (
                  <Button
                    size="md"
                    color="secondary"
                    onPress={() => visitWith({ status: null, window: "full" })}
                  >
                    Show full window
                  </Button>
                )
              }
            >
              No jobs in this window. Try a wider window or clear the status
              filter; the mirror refreshes from Salesforce on sync.
            </TableCard.Empty>
          ) : (
            <>
              <Table aria-label="Salesforce jobs">
                <Table.Header>
                  <Table.Head
                    id="name"
                    isRowHeader
                    label="Job"
                    className="w-full"
                  />
                  <Table.Head id="program" label="Program" />
                  <Table.Head id="account" label="Account" />
                  <Table.Head
                    id="start"
                    label="Start"
                    align="right"
                    sorted="asc"
                  />
                  <Table.Head id="status" label="Status" />
                  <Table.Head id="region" label="Region" />
                  <Table.Head id="attendance" label="Expected" align="right" />
                </Table.Header>
                <Table.Body items={jobs}>
                  {(job) => (
                    <Table.Row
                      id={job.id}
                      href={adminSalesforceJobPath(job.id)}
                    >
                      <Table.Lead>{job.name ?? job.salesforce_id}</Table.Lead>
                      <Table.Cell className="max-w-44 truncate">
                        {job.program_name ?? "—"}
                      </Table.Cell>
                      <Table.Cell>
                        <SchoolLink school={job.school} />
                      </Table.Cell>
                      <Table.When at={job.start_time} />
                      <Table.Status tier={job.status_color}>
                        {job.status}
                      </Table.Status>
                      <Table.Cell className="max-w-44 whitespace-normal">
                        {job.region ?? "—"}
                      </Table.Cell>
                      <Table.Figure>{job.expected_attendance}</Table.Figure>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table>
              <TableCard.Footer shown={jobs.length} total={counts.total} />
            </>
          )}
        </TableCard.Root>
      </div>
    </AdminLayout>
  )
}
